// Shared world constants and hex topology. The constants come from the
// generated gen/balance.ts — the same data/balance TOML the server's
// world package is generated from (R310) — and this module re-exports
// them so no import site changes. The topology functions below must
// match server/internal/world and docs/design/ui-map-interface.md §4.1.

export {
  DEFAULT_COLS, DEFAULT_ROWS, COL_PITCH, HEX_SIZE, MPX_PER_PX, PROTOCOL,
  BLOCK_TRENCH, BLOCK_RIDGE, FACTION_BASE_SONAR, FACTIONS, RESOURCES,
} from './gen/balance';
import {
  DEFAULT_COLS, DEFAULT_ROWS, COL_PITCH, HEX_SIZE, MPX_PER_PX, FACTIONS, RESOURCES,
} from './gen/balance';
// The neutral tint is the client-only lane (R312): the map's legend for
// anything unclaimed, generated from data/presentation/presentation.toml.
import { NEUTRAL_COLOR } from './gen/presentation';
export { NEUTRAL_COLOR };
import { world as S } from './gen/strings';

export const DEFAULT_WORLD = DEFAULT_COLS * COL_PITCH;

/** The world pitch of one grid row of a pointy-top odd-r grid, 1.5·s:
 *  `cellCenter` steps by this much, as `COL_PITCH` is the step of one
 *  column. Every reader of the row pitch takes it from here. */
export const ROW_PITCH = 1.5 * HEX_SIZE;

/** The height of a grid's playable rectangle: the bottom of the last row's
 *  hexes, which is the server's `Grid.WorldHeightPx` (R395). */
function worldHeight(rows: number): number {
  return ROW_PITCH * (rows - 1) + 2 * HEX_SIZE;
}

const DEFAULT_WORLD_H = worldHeight(DEFAULT_ROWS);

/** The world rectangle a grid of this size occupies, in world px: the
 *  server's `Grid.WorldPx` by `Grid.WorldHeightPx`, for a grid that is not
 *  the live one (the map designer's, an arcade map preview's). */
export function worldSize(cols: number, rows: number): { w: number; h: number } {
  return { w: cols * COL_PITCH, h: worldHeight(rows) };
}

// The grid is a per-world runtime value (R261, docs/design/arcade.md §3).
// One server process hosts the core world and small arcade worlds, and it
// sends the dimensions in `Hello.cols` / `Hello.rows`.
//
// COLS, ROWS, CELLS, WORLD and WORLD_H are the one mutable thing in this
// module. The server is the authority: `main.ts` must call `setGrid` from
// the Hello handler, before any layer reads them. `HEX_SIZE` and
// `COL_PITCH` do not depend on the dimensions and stay constant.
//
// WORLD is the width of the playable rectangle and WORLD_H is its height
// (R395). They are not the same figure: an arcade map may be much wider
// than it is tall. The core world is square to 8.4 px.
export let COLS = DEFAULT_COLS;
export let ROWS = DEFAULT_ROWS;
export let CELLS = DEFAULT_COLS * DEFAULT_ROWS;
export let WORLD = DEFAULT_WORLD;
export let WORLD_H = DEFAULT_WORLD_H;

/**
 * Applies the grid the server sent. `WORLD = cols * COL_PITCH`, which is the
 * server's `Grid.WorldPx`, and `WORLD_H` is its `Grid.WorldHeightPx`. Input
 * that is not finite or is less than 1 falls back to the core defaults.
 * Returns true when a value changed, so the caller knows it must re-fit the
 * layers that read the geometry one time.
 */
export function setGrid(cols: number, rows: number): boolean {
  const c = Number.isFinite(cols) && cols >= 1 ? Math.trunc(cols) : DEFAULT_COLS;
  const r = Number.isFinite(rows) && rows >= 1 ? Math.trunc(rows) : DEFAULT_ROWS;
  if (c === COLS && r === ROWS) return false;
  COLS = c;
  ROWS = r;
  CELLS = c * r;
  WORLD = c * COL_PITCH;
  WORLD_H = worldHeight(r);
  return true;
}

/**
 * The share of the core world's area that the grid now covers, in [0, 1].
 *
 * An ambience layer scatters a fixed count over the whole world rectangle.
 * That count is tuned against the core world, so a small arcade board — a
 * 12x10 map is about 1/164 of core's area — takes the same crowd on a
 * fraction of the water and reads as a blanket. Every such layer multiplies
 * its count by this share and keeps its own floor (R479). The value is
 * exactly 1 on the core world, so core is untouched.
 */
export function worldAreaShare(): number {
  const share = (WORLD * WORLD_H) / (DEFAULT_WORLD * DEFAULT_WORLD_H);
  if (!Number.isFinite(share) || share < 0) return 0;
  return Math.min(1, share);
}

/** Puts the core world's dimensions back. Tests use it to clean up. */
export function resetGrid(): void {
  setGrid(DEFAULT_COLS, DEFAULT_ROWS);
}

// The wire carries a position as integer millipixels (`MPX_PER_PX` to the
// world px). The first two are the conversion, unrounded: a reader of a
// frame takes the px as it falls. A sender rounds the figure it puts on the
// socket, and `wireMpx` is that one rounding.

/** World px to the wire's millipixels. */
export function pxToMpx(px: number): number { return px * MPX_PER_PX; }

/** World px as the whole millipixels a sender puts on the socket. */
export function wireMpx(px: number): number { return Math.round(px * MPX_PER_PX); }

/** World px from the wire's millipixels. */
export function mpxToPx(mpx: number): number { return mpx / MPX_PER_PX; }

// FACTION_BASE_SONAR (generated, above) is only used for *labelling*.
// Which circles a commander actually receives is decided by the server
// and sent on the wire, because the client got that rule wrong in both
// directions while it owned the decision: a ring for installations that
// granted the viewer nothing (R111) and an invented range for their own
// hold (R110).

export function resourceOf(id: number) {
  return RESOURCES.find((r) => r.id === id);
}

/** `#rrggbb` for the DOM overlay, which cannot take a packed number. The
 *  body lives in render/color; the importers of this module read it here. */
export { hex } from './render/color';

/** The faction row of `FACTIONS` with this id, or undefined for none. */
export function factionOf(id: number) {
  return FACTIONS.find((f) => f.id === id);
}

export function factionColor(id: number): number {
  return factionOf(id)?.color ?? NEUTRAL_COLOR;
}

export function factionName(id: number): string {
  if (id === 0) return S.unclaimed;
  return bannerNames.get(id) ?? factionOf(id)?.name ?? '?';
}

/** One banner of a Clan War world, as the hello sends it (R1260). */
export interface BannerLike { faction: number; name: string; tag: string; house: boolean }

// The banner names of a Clan War world (R1260). Every place that names a
// faction reads factionName, so the HUD, the roster, the legend and the
// tooltips name the banner's clan with no change of their own. The map
// is empty on every other world, which keeps the faction names.
const bannerNames = new Map<number, string>();

/** The name a clan carries on the chrome: an NPC house of a Clan War
 *  world reads "House <name>" (R1262), every other clan its own name. */
export function clanDisplayName(name: string, npc: boolean): string {
  return npc && bannerNames.size > 0 ? S.house_name({ name }) : name;
}

/** Install the banners of the hello; a hello with none clears them. */
export function setBanners(list: readonly BannerLike[] | undefined): void {
  bannerNames.clear();
  for (const b of list ?? []) {
    bannerNames.set(b.faction, b.house ? S.house_name({ name: b.name }) : `${b.name} [${b.tag}]`);
  }
}

// The deterministic PRNG for cosmetic scatter (ambience, speckle). The body
// is the leaf `ui/portrait/rng.ts`, which imports nothing, so the reel and
// the render worker take it without this module; the many readers of the
// map take it from here.
export { mulberry32 } from './ui/portrait/rng';

const SQRT3 = Math.sqrt(3);

// Direction k faces the edge between corners k and k+1 (corner k at 60k-30°).
const HEX_OFFSETS: [number, number][][] = [
  [[1, 0], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1]], // even rows
  [[1, 0], [1, 1], [0, 1], [-1, 0], [0, -1], [1, -1]],   // odd rows
];

// The three topology functions read the live grid. Each takes the dimensions
// as trailing parameters whose default is the live value, so a caller that
// must have another grid can give its own. JavaScript evaluates a default
// parameter for each call, so the value is always the current one.

/** The x of cell `i`'s centre alone. A per-frame caller reads the two
 *  scalars, because `cellCenter` makes a tuple on every call (§10). */
export function cellCenterX(i: number, cols = COLS): number {
  const col = i % cols, row = (i / cols) | 0;
  return SQRT3 * HEX_SIZE * (col + 0.5 * (row & 1) + 0.5);
}

/** The y of cell `i`'s centre alone. */
export function cellCenterY(i: number, cols = COLS): number {
  return ROW_PITCH * ((i / cols) | 0) + HEX_SIZE;
}

export function cellCenter(i: number, cols = COLS): [number, number] {
  return [cellCenterX(i, cols), cellCenterY(i, cols)];
}

export function hexCorner(k: number): [number, number] {
  const a = ((60 * k - 30) * Math.PI) / 180;
  return [HEX_SIZE * Math.cos(a), HEX_SIZE * Math.sin(a)];
}

export function neighborAt(i: number, k: number, cols = COLS, rows = ROWS): number {
  const col = i % cols, row = (i / cols) | 0;
  const [dx, dy] = HEX_OFFSETS[row & 1][k];
  const nc = col + dx, nr = row + dy;
  if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) return -1;
  return nr * cols + nc;
}

/**
 * The outline of a cell and the six cells round it (hex ring 1), as the
 * edges that border a cell outside the set, in world px: x0, y0, x1, y1
 * for each edge. A neighbour off the grid is left out of the set, so the
 * outline follows the grid edge. It is the capture ring of an arcade
 * structure (arcade.md §19.2, R2029): the designer and the match draw it.
 */
export function ringOneEdges(cell: number, cols = COLS, rows = ROWS): number[] {
  const set = [cell];
  for (let k = 0; k < 6; k++) {
    const n = neighborAt(cell, k, cols, rows);
    if (n >= 0) set.push(n);
  }
  const out: number[] = [];
  for (const c of set) {
    const cx = cellCenterX(c, cols), cy = cellCenterY(c, cols);
    for (let k = 0; k < 6; k++) {
      const n = neighborAt(c, k, cols, rows);
      if (n >= 0 && set.includes(n)) continue;
      const [ax, ay] = hexCorner(k);
      const [bx, by] = hexCorner((k + 1) % 6);
      out.push(cx + ax, cy + ay, cx + bx, cy + by);
    }
  }
  return out;
}

/**
 * The row and the column of the hex that holds the world point, with NO
 * grid check: a point outside the grid names the virtual cell that would
 * hold it, which is what the shaders' `cellAt` and the terrain raster read
 * at the world edge. `out` takes `[row, col]`, so a caller in a loop makes
 * no tuple; `cellAt` is the grid check on top of it.
 */
export function cellRowCol(x: number, y: number, out: [number, number]): void {
  const px = x - (SQRT3 * HEX_SIZE) / 2, py = y - HEX_SIZE;
  const qf = ((SQRT3 / 3) * px - py / 3) / HEX_SIZE;
  const rf = ((2 / 3) * py) / HEX_SIZE;
  let q = Math.round(qf), r = Math.round(rf);
  const s = Math.round(-qf - rf);
  const dq = Math.abs(q - qf), dr = Math.abs(r - rf), ds = Math.abs(s - (-qf - rf));
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  out[0] = r;
  out[1] = q + ((r - (r & 1)) >> 1);
}

/** `cellAt`'s own pair; it holds nothing between two calls. */
const ROW_COL: [number, number] = [0, 0];

export function cellAt(x: number, y: number, cols = COLS, rows = ROWS): number {
  cellRowCol(x, y, ROW_COL);
  const row = ROW_COL[0];
  if (row < 0 || row >= rows) return -1;
  const col = ROW_COL[1];
  if (col < 0 || col >= cols) return -1;
  return row * cols + col;
}
